import { Box, Grid as MuiGrid } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { memo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { GridContainer } from '~/components/GridContainer';
import { Typography } from '~/components/Typography';
import { EmptyState } from '~/components/empty-state';
import CreateDesignIllustration from '~/images/illustrations/create-show.svg';
import type {
  DesignList__Design as Design,
  DesignList__DesignFolder as Folder,
} from '../DesignList.generated';
import { StudioGridItem } from './StudioGridItem';

export interface StudioGridProps {
  designs: ReadonlyArray<Design>;
  folders: ReadonlyArray<Folder>;
  loading?: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const StudioGrid = memo(
  ({ designs, folders, loading, onCheck, selectedIds }: StudioGridProps) => {
    const { t } = useTranslation();

    return (
      <GridContainer
        loading={loading}
        isEmpty={folders.length === 0 && designs.length === 0}
        emptyState={
          <EmptyState
            illustration={CreateDesignIllustration}
            header={t('pages.studio.list.empty_state.header')}
            description={t('pages.studio.list.empty_state.description')}
          />
        }
      >
        {folders.length > 0 && (
          <Box
            sx={{
              paddingBottom: 4,
              marginBottom: 4,
              borderBottom: designs.length > 0 ? 1 : 0,
              borderBottomColor: 'borders.main',
            }}
          >
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {folders.length} {t('common.pluralize.folder_plural', { count: folders.length })}
            </Typography>
            <MuiGrid container spacing={2}>
              {folders.map((folder) => (
                <StudioGridItem
                  key={`DesignFolder-${folder.id}`}
                  item={folder}
                  onCheck={onCheck}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
        {designs.length > 0 && (
          <Box sx={{ paddingBottom: 4 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {designs.length} {t('common.pluralize.design_plural', { count: designs.length })}
            </Typography>
            <MuiGrid container spacing={2}>
              {designs.map((design) => (
                <StudioGridItem
                  key={design.id}
                  item={design}
                  onCheck={onCheck}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
      </GridContainer>
    );
  },
);

StudioGrid.displayName = 'StudioGrid';
